import React, { useRef, useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';

import {
    getDynamicFields,
    createDynamicField,
    updateDynamicField,
    deleteDynamicField,
    getDynamicFieldById
} from '../services/dynamicFieldService';

const DynamicFields = () => {
    const [fields, setFields] = useState([]);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formMode, setFormMode] = useState('create');
    const [selectedField, setSelectedField] = useState(null);
    
    // Get base URL for API
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
    
    // Helper function to get full icon URL
    const getIconUrl = (iconPath) => {
        if (!iconPath) return '';
        if (iconPath.startsWith('http')) return iconPath; // Already a full URL
        // Remove /api from the end and construct the full URL
        const baseUrl = API_BASE_URL.replace(/\/api$/, '');
        return `${baseUrl}${iconPath.startsWith('/') ? iconPath : '/' + iconPath}`;
    };
    
     const [formData, setFormData] = useState({
          label: '',
          value_key: '',
          description: '',
          icon: null,
          iconUrl: '', // URL of existing icon from server
          options: []
      });

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [search, setSearch] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const loadFields = async () => {
        setIsLoading(true);
        try {
            const params = { page, limit, search: search || undefined };
            const response = await getDynamicFields(params);
            
            const data = response?.data || response?.result || [];
            const meta = response?.meta || {};

            setFields(data);
            setTotalPages(meta.totalPages || 1);
            setTotalRecords(meta.total || data.length);
        } catch (error) {
            console.error('Failed to load fields:', error);
            setFields([]);
        } finally {
            setIsLoading(false);
        }
    };

    const prevSearch = useRef(search);
    useEffect(() => {
        const searchChanged = prevSearch.current !== search;
        prevSearch.current = search;

        const timer = setTimeout(() => {
            if (searchChanged && page !== 1) setPage(1);
            else loadFields();
        }, searchChanged ? 500 : 0);

        return () => clearTimeout(timer);
    }, [search, page, limit]); // loadFields is defined inside the component but stable across renders via page/limit/search deps

    // Handle string conversion for value mapping
    const generateValueKey = (val) => {
        return val.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    };

    const handleLabelChange = (e) => {
        const newLabel = e.target.value;
        if (formMode === 'create') {
            setFormData({ ...formData, label: newLabel, value_key: generateValueKey(newLabel) });
        } else {
            setFormData({ ...formData, label: newLabel });
        }
    };

      const handleCreate = () => {
          setFormMode('create');
          setFormData({ label: '', value_key: '', description: '', icon: null, iconUrl: '', options: [] });
          setIsFormOpen(true);
      };

    const handleView = async (field) => {
        try {
            // Fetch full field details to ensure we have all options
            const fieldData = await getDynamicFieldById(field.id);
            const fullField = fieldData?.data || fieldData?.result || field;
            console.log('Full field data:', fullField); // Debug log
            console.log('Options:', fullField.options); // Debug log
            setSelectedField(fullField);
        } catch (error) {
            console.error('Failed to load field details:', error);
            setSelectedField(field);
        }
        setIsViewModalOpen(true);
    };

    const handleEdit = async (field) => {
         setFormMode('edit');
         setSelectedField(field);
         setIsFormOpen(true);

         try {
             // Fetch full field details to ensure we have all options
             const fieldData = await getDynamicFieldById(field.id);
             const fullField = fieldData?.data || fieldData?.result || field;

              // Map options to ensure they have the correct structure
              // Handles both 'label/value' and 'option_label/option_value' naming conventions
              const mappedOptions = fullField.options?.map(opt => ({
                  id: opt.id,
                  label: opt.label || opt.option_label || '',
                  value: opt.value || opt.option_value || '',
                  description: opt.description || '',
                  color: opt.color || ''
              })) || [];

              setFormData({
                  label: fullField.label || '',
                  value_key: fullField.value_key || '',
                  description: fullField.description || '',
                  icon: null, // Icon is stored on server, not downloaded
                  iconUrl: fullField.icon || fullField.icon_url || '', // Load icon URL from server
                  options: mappedOptions
              });
             setSelectedField(fullField);
         } catch (error) {
             console.error('Failed to load field details:', error);
             // Fallback to the field data passed as parameter
             const mappedOptions = field.options?.map(opt => ({
                 id: opt.id,
                 label: opt.label || opt.option_label || '',
                 value: opt.value || opt.option_value || '',
                 description: opt.description || '',
                 color: opt.color || ''
             })) || [];

             setFormData({
                 label: field.label || '',
                 value_key: field.value_key || '',
                 description: field.description || '',
                 icon: null,
                 iconUrl: field.icon || field.icon_url || '',
                 options: mappedOptions
             });
         }
     };

    const handleDelete = (id) => {
        setItemToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDynamicField(itemToDelete);
            toast.success("Dynamic field deleted successfully");
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
            loadFields();
        } catch (error) {
            console.error('Failed to delete field: ', error);
        } finally {
            setIsDeleting(false);
        }
    };

     const handleAddOption = () => {
         setFormData({
             ...formData,
             options: [...formData.options, { tempId: Date.now() + Math.random(), label: '', value: '', description: '', color: '' }]
         });
     };

    const handleRemoveOption = (index) => {
        const newOptions = [...formData.options];
        newOptions.splice(index, 1);
        setFormData({ ...formData, options: newOptions });
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...formData.options];
        newOptions[index][field] = value;
        // No auto-generation - let user type their own value
        setFormData({ ...formData, options: newOptions });
    };

    const handleFormSubmit = async (e) => {
         e.preventDefault();

         // Remove empty options and filter out tempId and id
         const cleanedOptions = formData.options
             .filter(o => o.label.trim() !== '' && o.value.trim() !== '')
             .map(({ tempId, id, ...opt }) => opt); // Remove tempId and id from each option

         setIsSubmitting(true);
         try {
             if (formMode === 'create') {
                 // For create mode, options are sent with the main payload
                 let payload;

                 if (formData.icon && formData.icon instanceof File) {
                     // Use FormData for file upload
                     payload = new FormData();
                     payload.append('label', formData.label);
                     payload.append('value_key', formData.value_key);
                     payload.append('description', formData.description || '');
                     payload.append('icon', formData.icon);
                     payload.append('options', JSON.stringify(cleanedOptions));
                 } else {
                     payload = {
                         label: formData.label,
                         value_key: formData.value_key,
                         description: formData.description || '',
                         options: cleanedOptions
                     };
                 }
                 await createDynamicField(payload);
                 toast.success("Dynamic field created successfully");
              } else {
                  // For edit mode, send all options with the field update
                  let payload;

                  if (formData.icon && formData.icon instanceof File) {
                      payload = new FormData();
                      payload.append('label', formData.label);
                      payload.append('value_key', formData.value_key);
                      payload.append('description', formData.description || '');
                      payload.append('icon', formData.icon);
                      payload.append('options', JSON.stringify(cleanedOptions));
                  } else {
                      payload = {
                          label: formData.label,
                          value_key: formData.value_key,
                          description: formData.description || '',
                          options: cleanedOptions
                      };
                  }
                  await updateDynamicField(selectedField?.id, payload);

                 toast.success("Dynamic field updated successfully");
             }

            setIsFormOpen(false);
            loadFields();
        } catch (error) {
            console.error('Failed to create or update field: ', error);
            toast.error("Failed to save changes");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderPagination = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (page >= totalPages - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(page - 1);
                pages.push(page);
                pages.push(page + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <div style={{ background: '#F0F4F8', minHeight: '100vh' }}>
            <Topbar title="Dynamic Fields" subtitle="Manage global dynamic fields and options" />

            <div className="content">
                <div className="card" style={{ padding: '0px', overflow: 'hidden' }}>
                    <div className="toolbar" style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="search-wrap" style={{ flex: 1, maxWidth: '400px' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                            </svg>
                            <input
                                className="search-input"
                                placeholder="Search fields..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Create Field
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Field Label</th>
                                    <th>Value Key</th>
                                    <th># of Options</th>
                                    <th>Created At</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
                                            No fields found
                                        </td>
                                    </tr>
                                ) : (
                                    fields.map((field) => (
                                        <tr key={field.id}>
                                            <td><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{field.label}</div></td>
                                            <td><span style={{ fontSize: 13, color: 'var(--grey)', fontFamily: 'monospace', background: '#F1F5F9', padding: '4px 8px', borderRadius: 6 }}>{field.value_key}</span></td>
                                            <td>
                                                <span className="badge badge-active" style={{ background: 'rgba(102,178,255,0.15)', color: 'var(--blue)' }}>
                                                    {field.options?.length || 0} Options
                                                </span>
                                            </td>
                                            <td style={{ color: 'var(--grey)', fontSize: 12 }}>{formatDate(field.createdAt)}</td>
                                            <td>
                                                <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                    <button className="action-btn" title="View" onClick={() => handleView(field)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </button>
                                                    <button className="action-btn" title="Edit" onClick={() => handleEdit(field)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2-2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>
                                                    <button className="action-btn danger" title="Delete" onClick={() => handleDelete(field.id)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {fields.length > 0 && totalPages > 1 && (
                        <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
                            <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    className="btn btn-outline" disabled={page === 1} onClick={() => setPage(page - 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Previous
                                </button>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {renderPagination().map((p, i) => (
                                        <button
                                            key={i} className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                                            onClick={() => typeof p === 'number' && setPage(p)}
                                            style={{
                                                padding: '6px 12px', fontSize: 12, minWidth: 36,
                                                cursor: typeof p === 'number' ? 'pointer' : 'default',
                                                border: typeof p === 'number' ? '' : 'none', background: typeof p === 'number' ? '' : 'transparent'
                                            }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className="btn btn-outline" disabled={page === totalPages} onClick={() => setPage(page + 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* VIEW MODAL */}
            {isViewModalOpen && selectedField && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 500, padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>Field Details</h3>
                            <button onClick={() => setIsViewModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)' }}>✕</button>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ marginBottom: 24 }}>
                                <h2 style={{ margin: 0, fontSize: 22, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{selectedField.label}</h2>
                                <span style={{ fontFamily: 'monospace', fontSize: 13, background: '#F1F5F9', padding: '4px 8px', borderRadius: 4, marginTop: 10, display: 'inline-block' }}>{selectedField.value_key}</span>
                            </div>

                            <div className="field-row" style={{ border: 'none', display: 'block', padding: 0, marginBottom: 15 }}>
                                <div className="field-label" style={{ marginBottom: 10 }}>Options ({selectedField.options?.length || 0})</div>
                                <div style={{ background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0', maxHeight: 200, overflowY: 'auto' }}>
                                    {selectedField.options && selectedField.options.length > 0 ? (
                                        selectedField.options.map((opt, i) => (
                                            <div key={i} style={{ padding: '10px 14px', borderBottom: i < selectedField.options.length - 1 ? '1px solid #E2E8F0' : 'none', display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{opt.label || opt.option_label || ''}</span>
                                                <span style={{ color: 'var(--grey)', fontSize: 12, fontFamily: 'monospace' }}>{opt.value || opt.option_value || ''}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '15px', textAlign: 'center', color: 'var(--grey)', fontSize: 13 }}>No options defined</div>
                                    )}
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Created At</div>
                                    <div className="field-value">{formatDate(selectedField.createdAt)}</div>
                                </div>
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Field ID</div>
                                    <div className="field-value">#{selectedField.id}</div>
                                </div>
                            </div>

                            <div style={{ marginTop: 30, display: 'flex', gap: 12 }}>
                                <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsViewModalOpen(false)}>Close</button>
                                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { setIsViewModalOpen(false); handleEdit(selectedField); }}>Edit Field</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* FORM MODAL */}
            {isFormOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 550, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out', position: 'relative', zIndex: 10000 }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>
                                {formMode === 'create' ? 'Create Dynamic Field' : 'Update Dynamic Field'}
                            </h3>
                            <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)' }}>✕</button>
                        </div>
                        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <div style={{ padding: '24px', overflowY: 'auto', position: 'relative', zIndex: 1 }}>
                                <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Field Label <span style={{ color: '#EF4444' }}>*</span></label>
                                        <input
                                            className="form-input" required placeholder="e.g. Current Queue"
                                            value={formData.label} onChange={handleLabelChange} style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Value Key <span style={{ color: '#EF4444' }}>*</span></label>
                                        <input
                                            className="form-input" required placeholder="e.g. current_queue"
                                            value={formData.value_key} onChange={(e) => setFormData({ ...formData, value_key: e.target.value })} style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Description <span style={{ fontWeight: 400, color: 'var(--grey)' }}>(Optional)</span></label>
                                    <textarea
                                        className="form-input"
                                        placeholder="Enter field description..."
                                        value={formData.description || ''}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        style={{ width: '100%', minHeight: 60, paddingTop: 8 }}
                                    />
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Icon <span style={{ fontWeight: 400, color: 'var(--grey)' }}>(Optional)</span></label>
                                    <div
                                        onClick={() => document.getElementById('icon-upload').click()}
                                        style={{
                                            border: '2px dashed #D1D5DB',
                                            borderRadius: 8,
                                            padding: '40px 20px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            background: '#F9FAFB',
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            minHeight: 140,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#3B82F6';
                                            e.currentTarget.style.background = '#EFF6FF';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#D1D5DB';
                                            e.currentTarget.style.background = '#F9FAFB';
                                        }}
                                    >
                                        {formData.icon ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                                <img 
                                                    src={URL.createObjectURL(formData.icon)} 
                                                    alt="Icon preview" 
                                                    style={{ maxHeight: 80, maxWidth: 80, objectFit: 'contain' }}
                                                />
                                                <div style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>
                                                    ✓ {formData.icon.name}
                                                </div>
                                                <div style={{ fontSize: 12, color: 'var(--grey)' }}>Click to change icon</div>
                                            </div>
                                        ) : formData.iconUrl ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                                <img 
                                                    src={getIconUrl(formData.iconUrl)} 
                                                    alt="Existing icon" 
                                                    style={{ maxHeight: 80, maxWidth: 80, objectFit: 'contain' }}
                                                />
                                                <div style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>
                                                    ✓ Icon loaded
                                                </div>
                                                <div style={{ fontSize: 12, color: 'var(--grey)' }}>Click to change icon</div>
                                            </div>
                                        ) : (
                                            <>
                                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" style={{ margin: '0 auto 8px' }}>
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                    <polyline points="21 15 16 10 5 21" />
                                                </svg>
                                                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--navy)', marginBottom: 4 }}>Click to upload icon</div>
                                                <div style={{ fontSize: 12, color: 'var(--grey)' }}>PNG, JPG or SVG (Max. 2MB)</div>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        id="icon-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setFormData({ ...formData, icon: e.target.files?.[0] || null, iconUrl: '' })}
                                        style={{ display: 'none' }}
                                    />
                                </div>

                                <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>Options <span style={{ fontWeight: 400, color: 'var(--grey)' }}>(Optional)</span></label>
                                    <button type="button" onClick={handleAddOption} style={{ background: 'rgba(102,178,255,0.1)', color: 'var(--blue)', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        Add Option
                                    </button>
                                </div>

                                <div style={{ background: '#F8FAFC', padding: 16, borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {formData.options.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: 'var(--grey)', fontSize: 13, padding: '10px 0' }}>Click "Add Option" to define choices for this field.</div>
                                    ) : (
                                        formData.options.map((opt, i) => (
                                            <div key={opt.id || opt.tempId || i} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, background: 'white', border: '1px solid #E2E8F0', borderRadius: 6, position: 'relative', paddingTop: 40 }}>
                                                 {/* Remove button on top right */}
                                                 <button type="button" onClick={() => handleRemoveOption(i)} style={{ position: 'absolute', top: 10, right: 10, background: '#FEE2E2', color: '#EF4444', border: 'none', width: 32, height: 32, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: 18, padding: 0, fontWeight: 'bold', hover: { background: '#FECACA' }, transition: 'all 0.2s' }}>
                                                     ×
                                                 </button>

                                                 {/* Label and Value row */}
                                                 <div style={{ display: 'flex', gap: 10 }}>
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ fontSize: 11, color: 'var(--grey)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Label *</label>
                                                        <input
                                                            className="form-input" placeholder="Option Label (e.g. Short)" required
                                                            value={opt.label || ''} onChange={(e) => handleOptionChange(i, 'label', e.target.value)} style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ fontSize: 11, color: 'var(--grey)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Value *</label>
                                                        <input
                                                            className="form-input" placeholder="Option Value (e.g. short)" required
                                                            value={opt.value || ''} onChange={(e) => handleOptionChange(i, 'value', e.target.value)} style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Description - Full width */}
                                                <div>
                                                    <label style={{ fontSize: 11, color: 'var(--grey)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Description (Optional)</label>
                                                    <input
                                                        className="form-input" placeholder="Option description..."
                                                        value={opt.description || ''} onChange={(e) => handleOptionChange(i, 'description', e.target.value)} style={{ width: '100%' }}
                                                    />
                                                </div>

                                                 {/* Color - Below description */}
                                                 <div style={{ position: 'relative', zIndex: 10 }}>
                                                     <label style={{ fontSize: 11, color: 'var(--grey)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Color (Optional)</label>
                                                     <div style={{ display: 'flex', gap: 8 }}>
                                                         <input
                                                             type="color"
                                                             value={opt.color || '#ffffff'}
                                                             onChange={(e) => handleOptionChange(i, 'color', e.target.value)}
                                                             style={{ width: 50, height: 32, border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', position: 'relative', zIndex: 10001 }}
                                                         />
                                                         <input
                                                             className="form-input" placeholder="#ffffff" maxLength="7"
                                                             value={opt.color || ''} onChange={(e) => handleOptionChange(i, 'color', e.target.value)} style={{ flex: 1, fontSize: 11 }}
                                                         />
                                                     </div>
                                                 </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div style={{ padding: '20px 24px', borderTop: '1px solid #F1F5F9', background: '#fff', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                                <button type="button" className="btn btn-outline" onClick={() => setIsFormOpen(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ minWidth: 120 }} disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : (formMode === 'create' ? 'Create Field' : 'Save Changes')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {isDeleteModalOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 400, padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ padding: '30px 24px', textAlign: 'center' }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: '50%', background: '#FEE2E2',
                                color: '#EF4444', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', margin: '0 auto 20px'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                                </svg>
                            </div>
                            <h3 style={{ margin: '0 0 10px 0', fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>Delete Field?</h3>
                            <p style={{ margin: 0, fontSize: 14, color: 'var(--grey)', lineHeight: 1.5 }}>
                                Are you sure you want to remove this dynamic field? This action cannot be undone.
                            </p>
                        </div>
                        <div style={{ padding: '15px 24px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', gap: 12 }}>
                            <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</button>
                            <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center', background: '#EF4444', color: 'white', border: 'none' }} onClick={confirmDelete} disabled={isDeleting}>
                                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .content { padding: 24px; max-width: 1400px; margin: 0 auto; }
                .action-btns { display: flex; gap: 8px; }
                .badge-inactive { background: #FEE2E2; color: #991B1B; }
                .badge-active { background: #DCFCE7; color: #166534; }
                @media (max-width: 768px) {
                    .content { padding: 15px; }
                    .toolbar { flex-direction: column; gap: 12px; align-items: stretch !important; }
                    .search-wrap { maxWidth: 100% !important; }
                }
            `}</style>
            {(isLoading || isSubmitting || isDeleting) && <Spinner />}
        </div>
    );
};

export default DynamicFields;
